/**
 * Source attribution on orders (Düperthal fork, custom/attribution.md).
 *
 * Captures where a visitor came from (utm_*, ad click IDs, external referrer,
 * landing page) and writes it as cart attributes (_wbx_*), which Shopify
 * copies to the order (note_attributes / customAttributes). The backend
 * maps raw values to platforms later — no mapping happens here.
 *
 * - Last touch: new campaign parameters within a session replace every
 *   previous value.
 * - Lifetime: one browser session (session cookie wbx_attr, no Expires). Not
 *   sessionStorage, which would be per tab.
 * - Consent (CLAUDE.md rule 10): campaign values (utm_*, click IDs, external
 *   referrer) only with marketing consent
 *   (window.HY.consent from hy-consent.js), unless the theme setting
 *   attribution_without_consent is on — the same switch, read the same way,
 *   as hy-link-attribution.js (AP-270). Without consent only the touch type,
 *   timestamp, consent state and the landing page path are kept; campaign
 *   values of the landing page are added once consent is given on it.
 * - Landing page and referrer: origin/path only, never the query string — it
 *   may carry personal data (newsletter links: email=, _kx=, mc_eid=), and
 *   the known campaign parameters are attributes of their own anyway.
 * - The single leading underscore hides the attributes in checkout; a double
 *   underscore would make them private and unreadable for the Ajax API.
 * - Checkout safety: on submit of a cart form towards checkout, hidden
 *   attributes[...] inputs are added, so the POST to /cart stores them
 *   server-side before Shopify redirects — no waiting, nothing blocks.
 *
 * Everything runs inside try/catch: attribution must never break the page,
 * the cart or the checkout. Debug output only with ?wbx_debug=1.
 *
 * Pattern exception (CLAUDE.md JS): no custom element — a page-lifetime
 * bootstrap like hy-link-attribution.js.
 */

const CONFIG_ELEMENT = document.querySelector('script[data-hy-cart-attribution]');
const COOKIE_NAME = 'wbx_attr';
const DEBUG_COOKIE_NAME = 'wbx_debug';
const ATTRIBUTE_PREFIX = '_wbx_';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'li_fat_id'];
const ATTRIBUTE_KEYS = [
  ...UTM_KEYS,
  ...CLICK_ID_KEYS,
  'referrer',
  'landing_page',
  'touch_type',
  'touched_at',
  'consent_marketing',
];
// Referrers that never count as an external source: Shopify itself and
// payment providers the visitor returns from. Matches the host and its
// subdomains; extend as needed.
const IGNORED_REFERRERS = [
  'myshopify.com',
  'shopify.com',
  'shop.app',
  'paypal.com',
  'klarna.com',
  'klarna.net',
  'stripe.com',
  'mollie.com',
  'adyen.com',
  'sofort.com',
  'giropay.de',
  'unzer.com',
  'pay.google.com',
];
const MAX_VALUE = 255;
const MAX_URL = 500;
const MAX_COOKIE = 3800;

let debugEnabled = false;

function debug(...args) {
  // eslint-disable-next-line no-console
  if (debugEnabled) console.info('[wbx-attribution]', ...args);
}

function clean(value, max = MAX_VALUE) {
  return String(value == null ? '' : value).trim().slice(0, max);
}

function readCookie(name) {
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function writeCookie(name, value) {
  // No Expires/Max-Age: a session cookie, gone when the browser closes.
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax; Secure`;
}

function readRecord() {
  try {
    const raw = readCookie(COOKIE_NAME);
    const record = raw ? JSON.parse(raw) : null;
    return record && typeof record === 'object' ? record : null;
  } catch {
    return null;
  }
}

function fitsCookie(record) {
  return encodeURIComponent(JSON.stringify(record)).length <= MAX_COOKIE;
}

function writeRecord(record) {
  // A cookie over ~4 KB is dropped silently by the browser: shorten the URLs
  // first, then every value, so the record always survives.
  if (!fitsCookie(record)) {
    record.landing_page = clean(new URL(record.landing_page || '/', window.location.origin).pathname, MAX_URL);
    record.referrer = '';
  }
  if (!fitsCookie(record)) {
    Object.keys(record).forEach((key) => {
      record[key] = clean(record[key], 100);
    });
  }
  writeCookie(COOKIE_NAME, JSON.stringify(record));
}

function readConfig(element) {
  if (!element) return null;
  let cartAttributes = {};
  try {
    cartAttributes = JSON.parse(element.dataset.cartAttributes || '{}') || {};
  } catch {
    cartAttributes = {};
  }
  return {
    cartAttributes,
    itemCount: Number(element.dataset.itemCount) || 0,
    withoutConsent: element.dataset.withoutConsent === 'true',
  };
}

function hostMatches(host, domain) {
  return host === domain || host.endsWith(`.${domain}`);
}

function externalReferrer() {
  if (!document.referrer) return '';
  let url;
  try {
    url = new URL(document.referrer);
  } catch {
    return '';
  }
  const host = url.hostname.toLowerCase();
  if (host === window.location.hostname.toLowerCase()) return '';
  if (IGNORED_REFERRERS.some((domain) => hostMatches(host, domain))) return '';
  // Origin and path only: a referrer's query string may carry personal data.
  return clean(url.origin + url.pathname, MAX_URL);
}

function landingPage() {
  return clean(window.location.pathname, MAX_URL);
}

function consentState() {
  try {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    const visitor = privacy && typeof privacy.currentVisitorConsent === 'function'
      ? privacy.currentVisitorConsent()
      : null;
    if (visitor && visitor.marketing === 'yes') return 'granted';
    if (visitor && visitor.marketing === 'no') return 'denied';
  } catch {
    // API not loaded: consent is unknown.
  }
  return 'unknown';
}

function campaignAllowed(config) {
  if (config.withoutConsent) return true;
  const consent = window.HY && window.HY.consent;
  return Boolean(consent && consent.allowed('marketing'));
}

function campaignFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const values = {};
  [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = clean(params.get(key));
    if (value) values[key] = value;
  });
  return Object.keys(values).length > 0 ? values : null;
}

function buildRecord(touchType, touchedAt, campaign, allowed) {
  const record = {
    touch_type: touchType,
    touched_at: touchedAt,
    referrer: allowed ? externalReferrer() : '',
    landing_page: landingPage(),
  };
  if (allowed && campaign) Object.assign(record, campaign);
  return record;
}

function withoutCampaign(record) {
  return {
    touch_type: record.touch_type,
    touched_at: record.touched_at,
    consent_marketing: record.consent_marketing,
    referrer: '',
    // Path only, also for records written before the query was dropped.
    landing_page: clean(new URL(record.landing_page || '/', window.location.origin).pathname, MAX_URL),
  };
}

function desiredAttributes(record) {
  const attributes = {};
  ATTRIBUTE_KEYS.forEach((key) => {
    attributes[ATTRIBUTE_PREFIX + key] = clean(record ? record[key] : '', MAX_URL);
  });
  return attributes;
}

function differs(desired, current) {
  return Object.keys(desired).some((key) => (current[key] || '') !== desired[key]);
}

function cartUrl(path) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return `${root}${path}`;
}

function init() {
  const config = readConfig(CONFIG_ELEMENT);
  if (!config) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get('wbx_debug') === '1') writeCookie(DEBUG_COOKIE_NAME, '1');
  debugEnabled = readCookie(DEBUG_COOKIE_NAME) === '1';

  // 1. Decide the record for this page view (handover 5.2).
  const campaign = campaignFromUrl();
  let record = readRecord();
  let ownsRecord = false;
  if (campaign || !record) {
    // The touch type itself carries no personal data and is always kept.
    let touchType = 'direct';
    if (campaign) touchType = 'campaign';
    else if (externalReferrer()) touchType = 'referrer';
    record = buildRecord(touchType, new Date().toISOString(), campaign, campaignAllowed(config));
    ownsRecord = true;
  }
  record.consent_marketing = consentState();
  writeRecord(record);
  debug('record', record);

  // 2. Keep the cart in line with the record.
  let currentAttributes = config.cartAttributes;
  let itemCount = config.itemCount;
  let inFlight = false;
  let rerun = false;

  const sync = async () => {
    // One request at a time; a call during a request re-checks afterwards.
    if (inFlight) {
      rerun = true;
      return;
    }
    const desired = desiredAttributes(record);
    if (itemCount === 0 || !differs(desired, currentAttributes)) return;
    inFlight = true;
    try {
      const response = await fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: desired }),
      });
      if (response.ok) {
        const cart = await response.json();
        currentAttributes = cart.attributes || {};
        itemCount = cart.item_count || 0;
        debug('cart updated', currentAttributes);
      }
    } catch (error) {
      debug('sync failed', error);
    } finally {
      inFlight = false;
      if (rerun) {
        rerun = false;
        sync();
      }
    }
  };

  // Consent arrives after this module ran (hy-consent.js is async): record
  // the consent state and, once allowed, add the campaign values of this
  // landing page — but only if this page view set the record (last touch).
  const applyConsent = () => {
    try {
      const consentMarketing = consentState();
      if (!campaignAllowed(config)) {
        // Consent denied or withdrawn: drop campaign values kept earlier.
        record = withoutCampaign(record);
      } else if (ownsRecord) {
        record = buildRecord(record.touch_type, record.touched_at, campaign, true);
      }
      record.consent_marketing = consentMarketing;
      writeRecord(record);
      debug('consent applied', record);
      sync();
    } catch (error) {
      debug('consent failed', error);
    }
  };
  const consent = window.HY && window.HY.consent;
  if (consent && consent.ready) consent.ready.then(applyConsent);
  else sync();
  document.addEventListener('hy:consent:updated', applyConsent);

  // Follow every cart change: a cart that is created or replaced later (first
  // add, new cart after an order) starts without attributes, and an emptied
  // cart must not be synced. /cart/change.js responses (drawer, cart page)
  // carry the full cart; /cart/add.js responses only the added line, so the
  // cart is re-read then. This module's own update fires no event: no loop.
  document.addEventListener('hy:cart:updated', async (event) => {
    try {
      let cart = event.detail && event.detail.cart;
      if (!cart || typeof cart.item_count !== 'number' || !cart.attributes) {
        const response = await fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        cart = await response.json();
      }
      currentAttributes = cart.attributes || {};
      itemCount = cart.item_count || 0;
      sync();
    } catch (error) {
      debug('cart read failed', error);
    }
  });

  // Checkout: the cart form posts to /cart and Shopify redirects to checkout
  // afterwards; hidden inputs make that same POST store the attributes.
  document.addEventListener(
    'submit',
    (event) => {
      try {
        const form = event.target;
        const submitter = event.submitter;
        if (!(form instanceof HTMLFormElement) || !submitter || submitter.name !== 'checkout') return;
        if (!new URL(form.action, window.location.href).pathname.endsWith('/cart')) return;
        form.querySelectorAll('input[data-hy-cart-attribution-input]').forEach((input) => input.remove());
        Object.entries(desiredAttributes(record)).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `attributes[${key}]`;
          input.value = value;
          input.dataset.hyCartAttributionInput = '';
          form.append(input);
        });
        debug('checkout attributes added');
      } catch (error) {
        debug('checkout hook failed', error);
      }
    },
    true,
  );

  window.HY = window.HY || {};
  window.HY.attribution = {
    get: () => ({ record: { ...record }, attributes: desiredAttributes(record) }),
  };
}

try {
  init();
} catch {
  // Attribution must never break the page.
}
